import React from 'react';
import {
  Chip,
  Icon,
  Grid,
  Paper,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from '@mui/material';
import { Loader, Title, ReloadIconButton, TablePaginationWrapper } from '../../elements';
import { useTranslation } from 'react-i18next';
import { extractApiErrorMessage, notifications } from '../../utils';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import {
  HealthSystemDomainSlaughterPreparationsResultPreOrdersSummariesViewModel,
  HealthSystemDomainSlaughterPreparationsResultPreOrderDetailsViewModel
} from '../../api/HealthSystemApi';
import { useStyles } from './styles';
import { ApiService } from '../../hooks';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const SlaughterPreOrders: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const apiService = new ApiService();
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [preOrderSummaries, setPreOrderSummaries] = React.useState<
    HealthSystemDomainSlaughterPreparationsResultPreOrdersSummariesViewModel[]
  >([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);

  const now = new Date();

  const [selectedDateFrom, setSelectedDateFrom] = React.useState<Date | null>(
    new Date(now.getFullYear(), now.getMonth(), 1)
  );

  const [selectedDateTo, setSelectedDateTo] = React.useState<Date | null>(
    new Date(now.getFullYear(), now.getMonth() + 1, 0)
  );

  const [openDialog, setOpenDialog] = React.useState(false);
  const [summaryData, setSummaryData] =
    React.useState<HealthSystemDomainSlaughterPreparationsResultPreOrderDetailsViewModel | null>(null);

  const loadPreOrderSummaries = async (dateFrom: Date | null, dateTo: Date | null) => {
    const from = dateFrom?.toISOString() || new Date().toISOString();
    const to = dateTo?.toISOString() || new Date().toISOString();

    setIsBusy(true);
    apiService.api.slaughterPreparation
      .slaughterPreparationGetPreOrdersSummaries({ From: from, To: to, Page: page + 1, Size: rowsPerPage })
      .then((result) => {
        setPreOrderSummaries(result.data.data);
        setTotal(result.data.total);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  const handleDateChangeFrom = (date: unknown) => {
    setSelectedDateFrom(date as Date | null);
  };

  const handleDateChangeTo = (date: unknown) => {
    setSelectedDateTo(date as Date | null);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };

  const handleViewPreOrderSummary = (id: string) => {
    setIsBusy(true);
    apiService.api.slaughterPreparation
      .slaughterPreparationGetPreOrderDetails({ Id: id })
      .then((result) => {
        setSummaryData(result.data);
        setOpenDialog(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  const renderPreOrderSummaryDialog = () => (
    <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {t('f-pre-delivery-title')} {summaryData?.createdAt ? new Date(summaryData.createdAt).toLocaleDateString() : ''}
        <IconButton
          aria-label="close"
          onClick={() => setOpenDialog(false)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Icon>{'close'}</Icon>
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isBusy && <Loader isLoading={true} size={LoaderSize.DEFAULT_SIZE} />}
        {!isBusy && summaryData && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('f-pre-delivery-pre-delivery-amount')}{' '}
                <Box component="span" sx={{ fontWeight: 'normal' }}>
                  {summaryData.amount}
                </Box>
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('f-pre-delivery-food-chain-info')}
              </Typography>
              {Object.entries(summaryData.answers).map(([id, value]) => (
                <Grid container spacing={1} sx={{ alignItems: 'center', px: 3 }} key={id}>
                  <Grid size={{ xs: 11 }}>
                    <Typography>{id}</Typography>
                  </Grid>
                  <Grid size={{ xs: 1 }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {value === null ? '-' : value ? t('f-yes') : t('f-no')}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} gutterBottom>
                {t('f-pre-delivery-veterinary-info')}
              </Typography>

              <Grid container spacing={2} sx={{ px: 3 }}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body1">
                    <Box component="span" sx={{ fontWeight: 'medium' }}>
                      {t('f-pre-delivery-veterinary-name')}:
                    </Box>{' '}
                    {summaryData.veterinaryPracticeName ?? '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body1">
                    <Box component="span" sx={{ fontWeight: 'medium' }}>
                      {t('f-pre-delivery-veterinary-address')}:
                    </Box>{' '}
                    {summaryData.veterinaryAdress ?? '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('f-pre-delivery-pig-suppliers')}
              </Typography>
              {summaryData.pigletsSuppliers.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('f-pre-delivery-ubn')}</TableCell>
                      <TableCell>{t('f-pre-delivery-company-name')}</TableCell>
                      <TableCell>{t('f-pre-delivery-address')}</TableCell>
                      <TableCell>{t('f-country')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryData.pigletsSuppliers.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell>{s.ubn}</TableCell>
                        <TableCell>{s.name}</TableCell>
                        <TableCell>{s.address}</TableCell>
                        <TableCell>{s.country}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" sx={{ px: 3 }}>
                  —
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('f-pre-delivery-feed-suppliers')}
              </Typography>
              {summaryData.feedSuppliers.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('f-pre-delivery-ubn')}</TableCell>
                      <TableCell>{t('f-pre-delivery-company-name')}</TableCell>
                      <TableCell>{t('f-pre-delivery-address')}</TableCell>
                      <TableCell>{t('f-country')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryData.feedSuppliers.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell>{s.ubn}</TableCell>
                        <TableCell>{s.name}</TableCell>
                        <TableCell>{s.address}</TableCell>
                        <TableCell>{s.country}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setOpenDialog(false)} variant="contained">
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  React.useEffect(() => {
    if (!loaded) {
      loadPreOrderSummaries(selectedDateFrom, selectedDateTo);
    }
  }, [page, rowsPerPage, selectedDateFrom, selectedDateTo, loaded]);

  return (
    <Grid container spacing={1}>
      <Grid size={{ xs: 12, md: 12 }}>
        {/* title & spinner */}
        <Grid container direction="row" className={classes.paperHeader}>
          <Grid container size={{ xs: 10, md: 6 }} spacing={1}>
            <Grid>
              <Icon fontSize="large" color="primary">
                {'description'}
              </Icon>
            </Grid>
            <Grid>
              <Title>{t('f-pre-orders')}</Title>
            </Grid>
            <Grid>
              <Chip label={total} variant="outlined" />
            </Grid>
          </Grid>

          <Grid container className={classes.justifyRight} size={{ xs: 10, md: 6 }}>
            <Grid>
              <ReloadIconButton action={() => setLoaded(false)} />
            </Grid>
          </Grid>
        </Grid>
        <Grid container direction="row" sx={{ alignItems: 'center' }} spacing={1}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, md: 3, sm: 4 }}>
              <DesktopDatePicker
                className={` ${classes.width100} ${classes.pb12} `}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    id: 'dateFromSelect',
                    margin: 'normal',
                    'aria-label': 'change date'
                  }
                }}
                label={t('f-search-date-from')}
                value={selectedDateFrom}
                onChange={handleDateChangeFrom}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3, sm: 4 }}>
              <DesktopDatePicker
                className={` ${classes.width100} ${classes.pb12} `}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    id: 'dateToSelect',
                    margin: 'normal',
                    'aria-label': 'change date'
                  }
                }}
                label={t('f-search-date-to')}
                value={selectedDateTo}
                onChange={handleDateChangeTo}
              />
            </Grid>
          </LocalizationProvider>
        </Grid>
        <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

        {/* data table */}
        {loaded && (
          <Grid container spacing={3}>
            <Grid size={{ md: 12 }}>
              <TablePaginationWrapper
                rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              >
                <Paper elevation={1}>
                  <Table size="small" className={classes.resultsTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">{t('f-created-at')}</TableCell>
                        <TableCell align="center">{t('f-com-week-num')}</TableCell>
                        <TableCell align="center">{t('f-pre-delivery-amount-of-pigs')}</TableCell>
                        <TableCell align="center">{t('f-created-by')}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {preOrderSummaries.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell align="center">{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                          <TableCell align="center">{row.weekOfTheYear}</TableCell>
                          <TableCell align="center">{row.amount}</TableCell>
                          <TableCell align="center">{row.createdBy}</TableCell>
                          <TableCell align="right">
                            <IconButton color="primary" size="small" onClick={() => handleViewPreOrderSummary(row.id)}>
                              <Icon>{'visibility'}</Icon>
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    {renderPreOrderSummaryDialog()}
                  </Table>
                </Paper>
              </TablePaginationWrapper>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default SlaughterPreOrders;
